import { Button, Input, Table, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { formatMoney, getCurrencySymbol, priceStringAsNumber } from '../../../utils/format-money';
import { I18N_COMPONENTS } from '../constants';
import type { VariantPriceInput } from '../types';

const UOM_PATTERN = /^[a-zA-Z0-9-]+$/;

type PriceRowProps = {
  price: VariantPriceInput;
  index: number;
  currencyCode: string;
  canEdit: boolean;
  showUnitOfMeasure: boolean;
  isNew: boolean;
  isUnitOfMeasureRequired: boolean;
  isUnitOfMeasureDisabled: boolean;
  onSave: (price: VariantPriceInput) => void;
  onDelete: () => void;
};

type RowFormData = {
  quantity: string;
  price: string;
  unit_of_measure: string;
};

type RowErrors = {
  quantity?: string;
  price?: string;
  unit_of_measure?: string;
};

function formDataFromPrice(price: VariantPriceInput): RowFormData {
  return {
    quantity: String(price.quantity),
    price: String(price.price),
    unit_of_measure: price.unit_of_measure ?? '',
  };
}

export function PriceRow({
  price,
  index,
  currencyCode,
  canEdit,
  showUnitOfMeasure,
  isNew,
  isUnitOfMeasureRequired,
  isUnitOfMeasureDisabled,
  onSave,
  onDelete,
}: PriceRowProps) {
  const [editing, setEditing] = useState(isNew);
  const [formData, setFormData] = useState<RowFormData>(() => formDataFromPrice(price));
  const [errors, setErrors] = useState<RowErrors>({});

  const startEditing = () => {
    setFormData(formDataFromPrice(price));
    setErrors({});
    setEditing(true);
  };

  const save = () => {
    const nextErrors: RowErrors = {};
    const quantityNum = Number(formData.quantity);
    if (!formData.quantity.trim() || !Number.isInteger(quantityNum) || quantityNum < 1) {
      nextErrors.quantity = t(`${I18N_COMPONENTS}.price_row.errors.quantity`);
    }

    let priceNum: number | null = null;
    if (!formData.price.trim()) {
      nextErrors.price = t(`${I18N_COMPONENTS}.price_row.errors.price`);
    } else {
      try {
        priceNum = priceStringAsNumber(formData.price);
      } catch {
        nextErrors.price = t(`${I18N_COMPONENTS}.price_row.errors.price`);
      }
    }

    const uom = formData.unit_of_measure.trim();
    if (isUnitOfMeasureRequired) {
      if (!uom) {
        nextErrors.unit_of_measure = t(`${I18N_COMPONENTS}.price_row.errors.unit_of_measure`);
      } else if (!UOM_PATTERN.test(uom)) {
        nextErrors.unit_of_measure = t(
          `${I18N_COMPONENTS}.price_row.errors.unit_of_measure_pattern`,
        );
      }
    } else if (uom && !UOM_PATTERN.test(uom)) {
      nextErrors.unit_of_measure = t(`${I18N_COMPONENTS}.price_row.errors.unit_of_measure_pattern`);
    }

    setErrors(nextErrors);
    if (priceNum === null || Object.keys(nextErrors).length > 0) {
      return;
    }

    onSave({
      quantity: quantityNum,
      price: priceNum,
      tax_type: 'net',
      unit_of_measure: uom || undefined,
    });
    setEditing(false);
  };

  if (!editing) {
    return (
      <Table.Row id={`price-${price.quantity}-${index}`} index={index}>
        <Table.Cell>
          <Text>{`${price.quantity}+`}</Text>
        </Table.Cell>
        {showUnitOfMeasure && (
          <Table.Cell>
            {price.unit_of_measure ? (
              <Text>{price.unit_of_measure}</Text>
            ) : (
              <Text variant="secondary">{t('labels:none')}</Text>
            )}
          </Table.Cell>
        )}
        <Table.Cell>
          <Text>{formatMoney(price.price, currencyCode)}</Text>
        </Table.Cell>
        {canEdit && (
          <Table.Cell>
            <span className="flex items-center gap-3">
              <Button variant="secondary" size="sm" onClick={startEditing}>
                {t(`${I18N_COMPONENTS}.price_row.edit`)}
              </Button>
              <Button variant="dangerOutline" size="sm" onClick={onDelete}>
                {t(`${I18N_COMPONENTS}.price_row.remove`)}
              </Button>
            </span>
          </Table.Cell>
        )}
      </Table.Row>
    );
  }

  return (
    <Table.Row id={`edit-${price.quantity}-${index}`} index={index}>
      <Table.Cell>
        <Input
          label={t(`${I18N_COMPONENTS}.price_row.quantity`)}
          value={formData.quantity}
          onChange={(value) => setFormData((f) => ({ ...f, quantity: value }))}
          inputMode="numeric"
          short
          error={errors.quantity}
        />
      </Table.Cell>
      {showUnitOfMeasure && (
        <Table.Cell>
          <Input
            label={t(`${I18N_COMPONENTS}.price_row.unit_of_measure`)}
            value={formData.unit_of_measure}
            onChange={(value) => setFormData((f) => ({ ...f, unit_of_measure: value }))}
            placeholder={t(`${I18N_COMPONENTS}.price_row.unit_of_measure_placeholder`)}
            pattern="[a-zA-Z0-9\-]+"
            disabled={isUnitOfMeasureDisabled}
            error={errors.unit_of_measure}
          />
        </Table.Cell>
      )}
      <Table.Cell>
        <Input
          label={t(`${I18N_COMPONENTS}.price_row.price`)}
          value={formData.price}
          onChange={(value) => setFormData((f) => ({ ...f, price: value }))}
          prefix={getCurrencySymbol(currencyCode)}
          inputMode="decimal"
          error={errors.price}
        />
      </Table.Cell>
      <Table.Cell>
        <span className="flex items-center justify-end gap-3">
          <Button variant="secondary" size="sm" onClick={save}>
            {t(`${I18N_COMPONENTS}.price_row.save`)}
          </Button>
          <Button variant="secondary" onClick={() => (isNew ? onDelete() : setEditing(false))}>
            {t(`${I18N_COMPONENTS}.price_row.cancel`)}
          </Button>
        </span>
      </Table.Cell>
    </Table.Row>
  );
}
